import { useEffect, useRef, useState } from "react";
import { AlertCircleIcon, AlertTriangleIcon, CheckCircleIcon, MicIcon } from "lucide-react";

export type AudioLevelStatus = "idle" | "denied" | "error" | "low" | "weak" | "strong";

interface AudioLevelIndicatorProps {
    deviceId?: string;
    className?: string;
    onStatusChange?: (status: AudioLevelStatus) => void;
    /**
     * True when the selected mic is a known virtual / loopback device (by
     * label match). When true, the "No audio detected" warning is shown
     * even if the analyser briefly picks up system sound — the warning is
     * actually about the *device class*, so it shouldn't flicker as audio
     * level oscillates.
     */
    isVirtualMicSelected?: boolean;
}

type AudioStatus = "low" | "weak" | "strong";
type PermissionStatus = "idle" | "listening" | "denied" | "error";

// How long audioStatus must stay "low" before we surface the persistent
// "No audio detected" warning. Long enough to avoid flashing during the
// 1-2s analyser warmup after device switch / initial mount.
const LOW_AUDIO_WARNING_DELAY_MS = 3000;

export default function AudioLevelIndicator({ deviceId, className, onStatusChange, isVirtualMicSelected = false }: AudioLevelIndicatorProps) {
    const barRef = useRef<HTMLDivElement>(null);
    const [audioStatus, setAudioStatus] = useState<AudioStatus>("low");
    const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>("idle");
    // True once audioStatus has been "low" continuously for
    // LOW_AUDIO_WARNING_DELAY_MS while permissionStatus === "listening".
    // Used to surface a clear "try a different mic" warning for users who
    // pick a non-functional input (e.g. virtual loopback devices like
    // LoomAudioDevice that produce silence when used as a capture source).
    const [showLowAudioWarning, setShowLowAudioWarning] = useState(false);

    useEffect(() => {
        let audioCtx: AudioContext | null = null;
        let source: MediaStreamAudioSourceNode | null = null;
        let stream: MediaStream | null = null;
        let rafId: number | null = null;
        let lastStatusUpdate = 0;
        let cancelled = false;

        const setup = async () => {
            try {
                // First confirm there's at least one audio input device.
                // If the browser reports zero `audioinput` entries we can be
                // confident the device truly has no microphone (vs. just a
                // quiet one), so surface the hard-error state immediately
                // instead of waiting for getUserMedia to throw.
                if (navigator.mediaDevices?.enumerateDevices) {
                    try {
                        const devices = await navigator.mediaDevices.enumerateDevices();
                        const hasAudioInput = devices.some((d) => d.kind === "audioinput");
                        if (!hasAudioInput) {
                            if (!cancelled) setPermissionStatus("error");
                            return;
                        }
                    } catch {
                        // enumerateDevices itself failed — fall through to
                        // getUserMedia which will surface the real error.
                    }
                }

                stream = await navigator.mediaDevices.getUserMedia({
                    audio: deviceId ? { deviceId: { exact: deviceId } } : true,
                });

                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }

                const AudioContextClass =
                    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
                audioCtx = new AudioContextClass();
                if (audioCtx.state === "suspended") await audioCtx.resume();

                const analyser = audioCtx.createAnalyser();
                analyser.fftSize = 512;
                const dataArray = new Uint8Array(analyser.frequencyBinCount);

                source = audioCtx.createMediaStreamSource(stream);
                source.connect(analyser);
                setPermissionStatus("listening");

                const tick = () => {
                    analyser.getByteFrequencyData(dataArray);

                    let max = 0;
                    for (let i = 0; i < dataArray.length; i++) {
                        if (dataArray[i] > max) max = dataArray[i];
                    }
                    const percent = Math.round((max / 255) * 100);

                    // Direct DOM update for smooth 60fps bar animation
                    if (barRef.current) {
                        barRef.current.style.width = `${percent}%`;
                        barRef.current.style.backgroundColor =
                            percent < 15
                                ? "rgb(239 68 68)"   // red-500
                                : percent < 51
                                ? "rgb(234 179 8)"   // yellow-500
                                : "rgb(34 197 94)";  // green-500
                    }

                    // Throttle React state for status chip (~5fps is enough)
                    const now = Date.now();
                    if (now - lastStatusUpdate > 200) {
                        lastStatusUpdate = now;
                        setAudioStatus(
                            percent < 15 ? "low" : percent < 51 ? "weak" : "strong"
                        );
                    }

                    rafId = requestAnimationFrame(tick);
                };

                rafId = requestAnimationFrame(tick);
            } catch (err: unknown) {
                const name = err instanceof Error ? (err as { name?: string }).name : "";
                setPermissionStatus(
                    name === "NotAllowedError" || name === "PermissionDeniedError"
                        ? "denied"
                        : "error"
                );
            }
        };

        setup();

        return () => {
            cancelled = true;
            if (rafId !== null) cancelAnimationFrame(rafId);
            source?.disconnect();
            stream?.getTracks().forEach((t) => t.stop());
            audioCtx?.close();
        };
    }, [deviceId]);

    // Notify parent of effective status changes (combining permission + level).
    useEffect(() => {
        if (!onStatusChange) return;
        let effective: AudioLevelStatus;
        if (permissionStatus === "denied") effective = "denied";
        else if (permissionStatus === "error") effective = "error";
        else if (permissionStatus === "idle") effective = "idle";
        else effective = audioStatus;
        onStatusChange(effective);
    }, [audioStatus, permissionStatus, onStatusChange]);

    // Defer the "no audio detected" warning until the low state has been
    // sustained — protects against the analyser warmup window right after
    // device switch / initial mount where percent transiently reads 0.
    useEffect(() => {
        if (permissionStatus !== "listening" || audioStatus !== "low") {
            setShowLowAudioWarning(false);
            return;
        }
        const timer = setTimeout(() => {
            setShowLowAudioWarning(true);
        }, LOW_AUDIO_WARNING_DELAY_MS);
        return () => clearTimeout(timer);
    }, [audioStatus, permissionStatus, deviceId]);

    // Effective warning state. Combines the time-debounced audio-level signal
    // with the label-based virtual-mic flag so the warning doesn't flicker
    // when a virtual loopback device (e.g. Loom, OBS Virtual Audio)
    // momentarily picks up system sound.
    const showMicWarning =
        permissionStatus === "listening" &&
        (isVirtualMicSelected || (audioStatus === "low" && showLowAudioWarning));

    return (
        <div className={className ?? "p-4"}>
            {/* Header with Status Chip */}
            <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground">Current Audio Levels</span>

                {permissionStatus === "denied" && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <AlertCircleIcon className="h-3 w-3 text-red-600 dark:text-red-400" />
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">Mic access denied</span>
                    </div>
                )}
                {showMicWarning && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <AlertCircleIcon className="h-3 w-3 text-red-600 dark:text-red-400" />
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">No audio detected</span>
                    </div>
                )}
                {permissionStatus === "listening" && audioStatus === "weak" && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 hidden">
                        <AlertTriangleIcon className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
                        <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400">Weak audio</span>
                    </div>
                )}
                {permissionStatus === "listening" && audioStatus === "strong" && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 hidden">
                        <CheckCircleIcon className="h-3 w-3 text-green-600 dark:text-green-400" />
                        <span className="text-xs font-medium text-green-600 dark:text-green-400">Strong audio</span>
                    </div>
                )}
            </div>

            {/* Microphone Icon with Audio Level Bar */}
            <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-background border border-border flex items-center justify-center">
                    <MicIcon className="h-3 w-3 text-foreground" />
                </div>
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                        ref={barRef}
                        style={{ width: "0%", backgroundColor: "rgb(239 68 68)" }}
                        className="h-full"
                    />
                </div>
            </div>

            {showMicWarning && (
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                    We can&apos;t hear you. Try selecting a different microphone.
                </p>
            )}
        </div>
    );
}
